export type ActivityTableScreenKey =
  | "manager_dashboard_activities"
  | "manager_dashboard_projects"
  | "workflow_instance_detail_activities";

export type ActivityTableColumnKind = "default" | "form";

export interface ActivityTableColumnPreference {
  id: string;
  kind: ActivityTableColumnKind;
  label: string;
}

export interface ParsedFormFieldPair {
  label: string;
  value: string;
  normalizedLabel: string;
}

const toUtf8Mojibake = (value: string): string =>
  Array.from(new TextEncoder().encode(value), (byte) =>
    String.fromCharCode(byte)
  ).join("");

const withCedillaRecovered = (value: string): string =>
  toUtf8Mojibake(value).replace(/\u00c3\u00a7/g, "ç");

const ACTIVITY_TABLE_LABEL_REPLACEMENTS: Array<[string, string]> = [
  [toUtf8Mojibake("Bitiş"), "Bitiş"],
  [toUtf8Mojibake("Başlangıç"), "Başlangıç"],
  [withCedillaRecovered("Başlangıç"), "Başlangıç"],
  [toUtf8Mojibake("Çalış"), "Çalış"],
  [toUtf8Mojibake("Yönetici"), "Yönetici"],
  [toUtf8Mojibake("İ"), "İ"],
  [toUtf8Mojibake("ı"), "ı"],
  [toUtf8Mojibake("Ç"), "Ç"],
  [toUtf8Mojibake("ç"), "ç"],
  [toUtf8Mojibake("Ö"), "Ö"],
  [toUtf8Mojibake("ö"), "ö"],
  [toUtf8Mojibake("Ü"), "Ü"],
  [toUtf8Mojibake("ü"), "ü"],
  [toUtf8Mojibake("Ğ"), "Ğ"],
  [toUtf8Mojibake("ğ"), "ğ"],
  [toUtf8Mojibake("Ş"), "Ş"],
  [toUtf8Mojibake("ş"), "ş"],
];

export const sanitizeActivityTableLabel = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  let next = String(value).trim();
  ACTIVITY_TABLE_LABEL_REPLACEMENTS.forEach(([from, to]) => {
    next = next.split(from).join(to);
  });
  return next;
};

export const normalizeActivityTableAsciiLabel = (
  value?: string | null,
): string => {
  if (!value) {
    return "";
  }

  return sanitizeActivityTableLabel(value)
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
};

export const normalizeActivityTableLabel = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  return sanitizeActivityTableLabel(value)
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
};

export const createFormColumnId = (label: string): string =>
  `form:${normalizeActivityTableLabel(label)}`;

export const parseFormFieldPairs = (tooltip?: string | null): ParsedFormFieldPair[] => {
  if (!tooltip || !tooltip.trim()) {
    return [];
  }

  return tooltip
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(":");
      if (separatorIndex <= 0) {
        return null;
      }

      const label = line.slice(0, separatorIndex).trim();
      const value = line.slice(separatorIndex + 1).trim();
      const safeLabel = sanitizeActivityTableLabel(label);
      const normalizedLabel = normalizeActivityTableLabel(safeLabel);

      if (!safeLabel || !value || !normalizedLabel) {
        return null;
      }

      return {
        label: safeLabel,
        value,
        normalizedLabel,
      };
    })
    .filter((item): item is ParsedFormFieldPair => item !== null);
};

export const collectFormColumnPreferences = (
  tooltips: Array<string | null | undefined>,
): ActivityTableColumnPreference[] => {
  const byId = new Map<string, ActivityTableColumnPreference>();

  tooltips.forEach((tooltip) => {
    parseFormFieldPairs(tooltip).forEach((pair) => {
      const id = createFormColumnId(pair.label);
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          kind: "form",
          label: pair.label,
        });
      }
    });
  });

  return Array.from(byId.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "tr-TR"),
  );
};

export const getFormColumnValue = (
  tooltip: string | null | undefined,
  column: ActivityTableColumnPreference,
): string => {
  if (column.kind !== "form") {
    return "";
  }

  const normalizedTarget =
    normalizeActivityTableLabel(column.label) ||
    (column.id.startsWith("form:") ? column.id.slice(5).trim() : "");
  if (!normalizedTarget) {
    return "";
  }

  const values = parseFormFieldPairs(tooltip)
    .filter((pair) => pair.normalizedLabel === normalizedTarget)
    .map((pair) => pair.value.trim())
    .filter(Boolean);

  return Array.from(new Set(values)).join(" | ");
};

export const dedupeActivityTableColumns = (
  columns: ActivityTableColumnPreference[],
): ActivityTableColumnPreference[] => {
  const seen = new Set<string>();
  return columns.filter((column) => {
    const key =
      column.kind === "form"
        ? `${column.kind}:${normalizeActivityTableLabel(column.label)}`
        : `${column.kind}:${column.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
